import {
  faCalendar,
  faCalendarClock,
  faLocationDot,
  faSuitcaseRolling,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Trip } from '@/types/trip';
import { formatAddress } from '@/utils/address';
import { formatDateRange, tripDayCount } from '@/utils/schedule';
import { twx } from '@/utils/tailwind';

export interface TripHeaderProps {
  trip: Trip;
  className?: string;
}

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string | undefined };

const ICON_STYLE: FAIconStyle = {
  width: 11,
  height: 11,
  '--fa-primary-color': 'white',
  '--fa-secondary-color': 'rgba(255,255,255,0.85)',
};

export const TripHeader = ({ trip, className }: TripHeaderProps) => {
  const dayCount = tripDayCount(trip);
  const dateRange = formatDateRange(trip.startDate, trip.endDate);
  const totalPlaces = trip.places.length;
  const scheduled = trip.places.filter((p) => p.scheduledDay != null).length;
  const unscheduled = totalPlaces - scheduled;
  const location = formatAddress(trip.location);

  // Frosted chip. Layout:
  //   - On md+ : two rows (name + stats on top, location + date + days
  //     on bottom). Matches the search-bar chip's height profile.
  //   - On <md : everything stacks into a denser, narrower column so
  //     the chip fits next to the mode menu without truncating "United
  //     States" or pushing the actions chip onto its own line.
  return (
    <div
      className={twx(
        'min-w-0 flex flex-col justify-center gap-0.5 min-h-12 px-3 py-1.5 rounded-xl bg-secondary-focal/60 backdrop-blur-md border border-white/15 overflow-hidden',
        className,
      )}
    >
      {/* Title row — name + place stats. The collaboration/live indicator
          intentionally lives in the trip menu only (showing it here too
          felt redundant). */}
      <div className="flex items-center justify-between gap-3 min-w-0 leading-tight">
        <h2 className="font-diary text-base font-semibold leading-tight text-white truncate min-w-0 flex items-center gap-1.5">
          {/* `key={trip.name}` remounts the span on every rename so the
              spring keyframe replays — a small moment of "yes, that
              changed" without any imperative animation triggering. */}
          <span
            key={trip.name}
            className="motion-safe:animate-kawaii-name-spring inline-block truncate min-w-0"
          >
            {trip.name}
          </span>
        </h2>
        <div className="hidden md:flex shrink-0 items-center gap-1 text-[11px] font-medium text-white/85">
          <FontAwesomeIcon icon={faSuitcaseRolling} style={ICON_STYLE} />
          <span>
            {scheduled}/{totalPlaces}
          </span>
          {unscheduled > 0 && (
            <span className="inline-flex items-center gap-1" title={`${unscheduled} unscheduled`}>
              <span className="text-white/35" aria-hidden>
                ·
              </span>
              <FontAwesomeIcon icon={faCalendarClock} style={ICON_STYLE} />
              {unscheduled}
            </span>
          )}
        </div>
      </div>

      {/* Metadata — stacks vertically on <md so the chip can grow tall (it
          still fits next to the mode menu in the header's flex row), then
          flows back into a single line at md+. */}
      <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-2 text-[11px] text-white/75 min-w-0 leading-tight">
        {location && (
          <span className="inline-flex items-center gap-1 min-w-0 truncate md:max-w-[40%]">
            <FontAwesomeIcon icon={faLocationDot} style={ICON_STYLE} />
            <span className="truncate">{location}</span>
          </span>
        )}
        {location && (
          <span className="hidden md:inline text-white/35" aria-hidden>
            ·
          </span>
        )}
        <span className="inline-flex items-center gap-1 min-w-0 truncate">
          <FontAwesomeIcon icon={faCalendar} style={ICON_STYLE} />
          <span className="truncate">{dateRange}</span>
          <span className="md:hidden inline text-white/35" aria-hidden>
            ·
          </span>
          <span className="md:hidden inline">
            {dayCount} {dayCount === 1 ? 'day' : 'days'}
          </span>
        </span>
        <span className="hidden md:inline text-white/35 shrink-0" aria-hidden>
          ·
        </span>
        <span className="hidden md:inline shrink-0">
          {dayCount} {dayCount === 1 ? 'day' : 'days'}
        </span>
      </div>
    </div>
  );
};
